import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Store, RefreshCw, X, Upload, Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VendorRegistrationsPage = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [menuCardModal, setMenuCardModal] = useState({ open: false, photos: [], vendorName: '' });
  const [aadhaarModal, setAadhaarModal] = useState({ open: false, photo: '', vendorName: '' });
  const [restaurantImageModal, setRestaurantImageModal] = useState({ open: false, photo: '', vendorName: '' });

  // Edit Vendor state
  const [editVendor, setEditVendor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const handleSaveVendor = async () => {
    setEditSaving(true);
    try {
      const payload = {
        vendor_id: editVendor.id,
        name: editForm.name,
        whatsapp_number: editForm.whatsapp_number,
        description: editForm.description,
        categories: editForm.categories,
        location: editForm.location,
        fssai_number: editForm.fssai_number,
        owner_aadhar_number: editForm.owner_aadhar_number,
      };
      // Only send images if changed (new base64 data)
      if (editForm.owner_aadhar_photo_url !== (editVendor.owner_aadhar_photo_url || '')) {
        payload.owner_aadhar_photo_url = editForm.owner_aadhar_photo_url;
      }
      if (editForm.image_url !== (editVendor.image_url || '')) {
        payload.image_url = editForm.image_url;
      }
      if (JSON.stringify(editForm.menu_card_photos) !== JSON.stringify(editVendor.menu_card_photos || [])) {
        payload.menu_card_photos = editForm.menu_card_photos;
      }

      await axios.patch(`${API}/vendors/update`, payload);
      toast.success('Vendor updated successfully');
      setEditVendor(null);
      fetchVendors();
    } catch (err) {
      console.error('[EDIT_VENDOR] Failed:', err);
      toast.error('Failed to update vendor');
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = vendors.filter(vendor =>
        vendor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.whatsapp_number?.includes(searchQuery) ||
        vendor.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [searchQuery, vendors]);

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/vendors`, { withCredentials: true });
      setVendors(response.data);
      setFilteredVendors(response.data);
    } catch (err) {
      console.error('Failed to load vendors:', err);
      if (err.response?.status === 403) {
        setError('Admin access required to view vendor registrations.');
      } else if (err.response?.status === 401) {
        setError('Please login to access this page.');
      } else {
        setError('Unable to load vendor registrations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (vendorId) => {
    try {
      await axios.post(`${API}/vendors/${vendorId}/approve`, {}, { withCredentials: true });
      toast.success('Vendor approved');
      fetchVendors();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to approve vendor');
      }
    }
  };

  const handleToggleActive = async (vendorId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await axios.patch(`${API}/vendors/${vendorId}/active`, { is_active: newStatus }, { withCredentials: true });
      toast.success(`Vendor set to ${newStatus ? 'Active' : 'Inactive'}`);
      fetchVendors();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to update vendor status');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-green-600 font-semibold">Loading vendor registrations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white rounded-xl shadow-md p-8 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={fetchVendors} className="bg-green-600 hover:bg-green-700 text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            <Button onClick={() => { window.location.href = '/login?redirect=%2Fadmin%2Fvendor-registrations'; }} variant="outline">
              Switch Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-white/20 rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold">Vendor Registrations</h1>
              <p className="text-green-100">Manage vendor approvals</p>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
            <Store className="w-8 h-8" />
            <div>
              <p className="text-2xl font-bold">{vendors.length}</p>
              <p className="text-sm text-green-100">Total Vendors</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <Input
            type="text"
            placeholder="Search by name, phone, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
            data-testid="search-input"
          />
        </div>

        {/* Vendors Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {filteredVendors.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchQuery ? 'No vendors found matching your search' : 'No vendors registered yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      WhatsApp Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      FSSAI Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Aadhaar Card Photo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Menu Card Photo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Restaurant Image
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Bulk Import
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Edit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Active / Inactive
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredVendors.map((vendor, index) => (
                    <tr
                      key={vendor.id || index}
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`vendor-row-${index}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{vendor.name}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{vendor.whatsapp_number || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{vendor.location || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700 font-mono">{vendor.fssai_number || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {vendor.owner_aadhar_photo_url ? (
                          <img
                            src={vendor.owner_aadhar_photo_url}
                            alt="Aadhaar"
                            className="w-12 h-8 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setAadhaarModal({ open: true, photo: vendor.owner_aadhar_photo_url, vendorName: vendor.name })}
                            data-testid={`aadhaar-photo-${index}`}
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {vendor.menu_card_photos && vendor.menu_card_photos.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <img
                              src={vendor.menu_card_photos[0]}
                              alt="Menu Card"
                              className="w-12 h-8 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setMenuCardModal({ open: true, photos: vendor.menu_card_photos, vendorName: vendor.name })}
                              data-testid={`menu-card-photo-${index}`}
                            />
                            {vendor.menu_card_photos.length > 1 && (
                              <button
                                onClick={() => setMenuCardModal({ open: true, photos: vendor.menu_card_photos, vendorName: vendor.name })}
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                +{vendor.menu_card_photos.length - 1} more
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {vendor.image_url ? (
                          <img
                            src={vendor.image_url}
                            alt="Restaurant"
                            className="w-12 h-8 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setRestaurantImageModal({ open: true, photo: vendor.image_url, vendorName: vendor.name })}
                            data-testid={`restaurant-image-${index}`}
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">No Image</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          vendor.status === 'APPROVED' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {vendor.status === 'APPROVED' ? 'Approved' : 'Under Review'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(vendor.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/admin/vendor-registrations/${vendor.id}/bulk-import?name=${encodeURIComponent(vendor.name)}`)}
                          disabled={vendor.status !== 'APPROVED'}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                            vendor.status === 'APPROVED'
                              ? 'bg-blue-500 hover:bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                          data-testid={`import-btn-${index}`}
                        >
                          <Upload className="w-3 h-3" />
                          Import
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {vendor.status !== 'APPROVED' ? (
                          <button
                            onClick={() => handleApprove(vendor.id)}
                            className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors"
                            data-testid={`approve-btn-${index}`}
                          >
                            Approve
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setEditVendor(vendor);
                            setEditForm({
                              name: vendor.name || '',
                              whatsapp_number: vendor.whatsapp_number || '',
                              description: vendor.description || '',
                              categories: (vendor.categories || []).join(', '),
                              location: vendor.location || '',
                              fssai_number: vendor.fssai_number || '',
                              owner_aadhar_number: vendor.owner_aadhar_number || '',
                              owner_aadhar_photo_url: vendor.owner_aadhar_photo_url || '',
                              menu_card_photos: vendor.menu_card_photos || [],
                              image_url: vendor.image_url || '',
                            });
                          }}
                          className="px-3 py-1 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-1"
                          data-testid={`edit-btn-${index}`}
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(vendor.id, vendor.is_active !== false)}
                          className={`px-3 py-1 text-sm rounded-full font-medium transition-colors ${
                            vendor.is_active !== false
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`toggle-active-btn-${index}`}
                        >
                          {vendor.is_active !== false ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Showing {filteredVendors.length} of {vendors.length} vendors
        </div>
      </div>

      {/* Edit Vendor Modal */}
      {editVendor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setEditVendor(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Edit Vendor — {editVendor.name}</h3>
              <button onClick={() => setEditVendor(null)} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Text Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-vendor-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number *</label>
                <Input value={editForm.whatsapp_number} onChange={(e) => setEditForm({ ...editForm, whatsapp_number: e.target.value })} data-testid="edit-vendor-whatsapp" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="w-full p-2 border rounded-lg text-sm min-h-[80px]" data-testid="edit-vendor-description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categories (comma separated)</label>
                <Input value={editForm.categories} onChange={(e) => setEditForm({ ...editForm, categories: e.target.value })} data-testid="edit-vendor-categories" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="edit-vendor-location" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">FSSAI Number</label>
                  <Input value={editForm.fssai_number} onChange={(e) => setEditForm({ ...editForm, fssai_number: e.target.value })} data-testid="edit-vendor-fssai" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Number</label>
                  <Input value={editForm.owner_aadhar_number} onChange={(e) => setEditForm({ ...editForm, owner_aadhar_number: e.target.value })} data-testid="edit-vendor-aadhaar" />
                </div>
              </div>

              {/* Image: Aadhaar Card Photo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Card Photo</label>
                {editForm.owner_aadhar_photo_url && (
                  <img src={editForm.owner_aadhar_photo_url} alt="Aadhaar" className="w-32 h-20 object-cover rounded-lg border mb-2" />
                )}
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => setEditForm({ ...editForm, owner_aadhar_photo_url: reader.result });
                    reader.readAsDataURL(file);
                  }
                }} className="w-full text-sm" data-testid="edit-vendor-aadhaar-photo" />
                <p className="text-xs text-gray-400 mt-1">{editForm.owner_aadhar_photo_url ? 'Choose file to replace' : 'No image'}</p>
              </div>

              {/* Image: Menu Card Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Menu Card Photos</label>
                {editForm.menu_card_photos.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {editForm.menu_card_photos.map((photo, i) => (
                      <img key={i} src={photo} alt={`Menu ${i+1}`} className="w-20 h-14 object-cover rounded border" />
                    ))}
                  </div>
                )}
                <input type="file" accept="image/*" multiple onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const readers = files.map(file => new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                  }));
                  Promise.all(readers).then(results => setEditForm({ ...editForm, menu_card_photos: results }));
                }} className="w-full text-sm" data-testid="edit-vendor-menu-photos" />
                <p className="text-xs text-gray-400 mt-1">{editForm.menu_card_photos.length > 0 ? `${editForm.menu_card_photos.length} photo(s) — choose files to replace` : 'No photos'}</p>
              </div>

              {/* Image: Restaurant Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Image</label>
                {editForm.image_url && (
                  <img src={editForm.image_url} alt="Restaurant" className="w-32 h-20 object-cover rounded-lg border mb-2" />
                )}
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => setEditForm({ ...editForm, image_url: reader.result });
                    reader.readAsDataURL(file);
                  }
                }} className="w-full text-sm" data-testid="edit-vendor-restaurant-image" />
                <p className="text-xs text-gray-400 mt-1">{editForm.image_url ? 'Choose file to replace' : 'No image'}</p>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex gap-3 p-4 border-t sticky bottom-0 bg-white">
              <Button
                onClick={handleSaveVendor}
                disabled={editSaving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="edit-vendor-save"
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditVendor(null)} className="flex-1" data-testid="edit-vendor-cancel">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Aadhaar Card Photo Modal */}
      {aadhaarModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setAadhaarModal({ open: false, photo: '', vendorName: '' })}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Aadhaar Card Photo - {aadhaarModal.vendorName}</h3>
              <button
                onClick={() => setAadhaarModal({ open: false, photo: '', vendorName: '' })}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img src={aadhaarModal.photo} alt="Aadhaar Card" className="w-full h-auto rounded-lg" />
            </div>
          </div>
        </div>
      )}

      {/* Menu Card Photos Modal */}
      {menuCardModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setMenuCardModal({ open: false, photos: [], vendorName: '' })}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Menu Card Photos - {menuCardModal.vendorName} ({menuCardModal.photos.length})</h3>
              <button
                onClick={() => setMenuCardModal({ open: false, photos: [], vendorName: '' })}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {menuCardModal.photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img src={photo} alt={`Menu Card ${index + 1}`} className="w-full h-auto rounded-lg border" />
                  <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{index + 1} / {menuCardModal.photos.length}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Restaurant Image Modal */}
      {restaurantImageModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setRestaurantImageModal({ open: false, photo: '', vendorName: '' })}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Restaurant Image - {restaurantImageModal.vendorName}</h3>
              <button
                onClick={() => setRestaurantImageModal({ open: false, photo: '', vendorName: '' })}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img src={restaurantImageModal.photo} alt="Restaurant" className="w-full h-auto rounded-lg" />
              <div className="flex gap-3 mt-4">
                <a
                  href={restaurantImageModal.photo}
                  download={`restaurant-${restaurantImageModal.vendorName}.jpg`}
                  className="flex-1 text-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                  data-testid="download-restaurant-image"
                >
                  Download
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(restaurantImageModal.photo);
                    toast.success('Image link copied!');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
                  data-testid="copy-restaurant-image-link"
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorRegistrationsPage;
